import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, AlertTriangle, Cake, School, Clock, X, MessageCircle, Eye, EyeOff, UserPlus, FileText } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard({ onLogout }) {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [mensalidades, setMensalidades] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [preMatriculasPendentes, setPreMatriculasPendentes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [alertasFaltasOcultos, setAlertasFaltasOcultos] = useState([]);
  const [mostrarAlertaFaltas, setMostrarAlertaFaltas] = useState(true);

  useEffect(() => {
    fetchDashboard();
    fetchMensalidades();
    fetchUserProfile();
    fetchPreMatriculasPendentes();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/dashboard');
      setDashboard(response.data);
    } catch (error) {
      // Erro já tratado pelo interceptor
    } finally {
      setLoading(false);
    }
  };

  const fetchMensalidades = async () => {
    try {
      const response = await api.get('/mensalidades/dashboard');
      setMensalidades(response.data);
    } catch (error) {
      // Erro já tratado pelo interceptor
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await api.get('/perfil/escola');
      setUserProfile(response.data);
    } catch (error) {
      // Erro já tratado pelo interceptor
    }
  };

  const fetchPreMatriculasPendentes = async () => {
    try {
      const response = await api.get('/pre-matriculas/pendentes/count');
      setPreMatriculasPendentes(response.data.count);
    } catch (error) {
      // Erro já tratado pelo interceptor
    }
  };

  if (loading) {
    return <Layout onLogout={onLogout}><div className="text-center py-12">Carregando...</div></Layout>;
  }

  return (
    <Layout onLogout={onLogout}>
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Dashboard</h1>
          <p className="text-gray-600">Visão geral da escola</p>
        </div>

        {/* Banner de Período de Teste - Design mais delicado */}
        {userProfile && !userProfile.is_admin && !userProfile.is_active && userProfile.trial_days_remaining >= 0 ? (
          <div 
            className={`rounded-lg p-4 ${
              userProfile.trial_expired 
                ? 'bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200' 
                : 'bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200'
            }`}
            data-testid="trial-alert"
          >
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-full ${
                userProfile.trial_expired ? 'bg-amber-100' : 'bg-blue-100'
              }`}>
                <Clock className={`h-5 w-5 ${
                  userProfile.trial_expired ? 'text-amber-600' : 'text-blue-600'
                }`} />
              </div>
              <div className="flex-1">
                <h3 className={`font-medium ${
                  userProfile.trial_expired ? 'text-amber-800' : 'text-blue-800'
                }`}>
                  {userProfile.trial_expired 
                    ? 'Período de avaliação finalizado' 
                    : 'Você está em período de avaliação'}
                </h3>
                <p className={`text-sm mt-1 ${
                  userProfile.trial_expired ? 'text-amber-700' : 'text-blue-700'
                }`}>
                  {userProfile.trial_expired ? (
                    'Gostou do sistema? Entre em contato para ativar sua conta e continuar usando todas as funcionalidades.'
                  ) : (
                    <>
                      Restam <strong>{userProfile.trial_days_remaining} dia{userProfile.trial_days_remaining !== 1 ? 's' : ''}</strong> para você conhecer o sistema. 
                      Explore todas as funcionalidades!
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        ) : null}

        {/* Alerta de Pré-Matrículas Pendentes */}
        {preMatriculasPendentes > 0 && (
          <Alert className="border-purple-200 bg-purple-50" data-testid="alert-pre-matriculas">
            <UserPlus className="h-5 w-5 text-purple-600" />
            <AlertTitle className="text-purple-800 font-semibold">
              {preMatriculasPendentes} Pré-Matrícula{preMatriculasPendentes > 1 ? 's' : ''} Pendente{preMatriculasPendentes > 1 ? 's' : ''}!
            </AlertTitle>
            <AlertDescription className="text-purple-700">
              <p className="text-sm mb-3">Existem novas solicitações de matrícula aguardando sua aprovação.</p>
              <Button 
                size="sm" 
                className="bg-purple-600 hover:bg-purple-700"
                onClick={() => navigate('/pre-matriculas')}
              >
                <FileText className="w-4 h-4 mr-2" />
                Ver Pré-Matrículas
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-6">
          <Card className="flex-1 min-w-[140px] max-w-[50%] border-l-4 border-l-blue-500 shadow-md hover:shadow-lg transition-shadow md:max-w-none md:flex-none md:w-auto" data-testid="card-total-alunos">
            <CardHeader className="p-3 pb-1 sm:px-4 sm:pt-4 sm:pb-2">
              <CardTitle className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-600 flex items-center gap-1">
                <Users className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                Alunos
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0 sm:px-4 sm:pb-4">
              <div className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-600" data-testid="total-alunos-count">{dashboard?.total_alunos || 0}</div>
            </CardContent>
          </Card>

          <Card className="flex-1 min-w-[140px] max-w-[50%] border-l-4 border-l-green-500 shadow-md hover:shadow-lg transition-shadow md:max-w-none md:flex-none md:w-auto" data-testid="card-total-turmas">
            <CardHeader className="p-3 pb-1 sm:px-4 sm:pt-4 sm:pb-2">
              <CardTitle className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-600 flex items-center gap-1">
                <School className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                Turmas
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0 sm:px-4 sm:pb-4">
              <div className="text-xl sm:text-2xl md:text-3xl font-bold text-green-600" data-testid="total-turmas-count">{dashboard?.total_turmas || 0}</div>
            </CardContent>
          </Card>

          <Card className="flex-1 min-w-[140px] max-w-[50%] border-l-4 border-l-orange-500 shadow-md hover:shadow-lg transition-shadow md:max-w-none md:flex-none md:w-auto" data-testid="card-aniversariantes">
            <CardHeader className="p-3 pb-1 sm:px-4 sm:pt-4 sm:pb-2">
              <CardTitle className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-600 flex items-center gap-1">
                <Cake className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                Anivers.
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0 sm:px-4 sm:pb-4">
              <div className="text-xl sm:text-2xl md:text-3xl font-bold text-orange-600" data-testid="aniversariantes-count">{dashboard?.aniversariantes?.length || 0}</div>
            </CardContent>
          </Card>

          <Card className="flex-1 min-w-[140px] max-w-[50%] border-l-4 border-l-red-500 shadow-md hover:shadow-lg transition-shadow md:max-w-none md:flex-none md:w-auto" data-testid="card-alunos-faltosos">
            <CardHeader className="p-3 pb-1 sm:px-4 sm:pt-4 sm:pb-2">
              <CardTitle className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-600 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                3+ Faltas
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0 sm:px-4 sm:pb-4">
              <div className="text-xl sm:text-2xl md:text-3xl font-bold text-red-600" data-testid="faltosos-count">{dashboard?.alunos_faltosos?.length || 0}</div>
            </CardContent>
          </Card>
        </div>

        {dashboard?.aniversariantes && dashboard.aniversariantes.length > 0 && (
          <Alert className="border-orange-200 bg-orange-50" data-testid="alert-aniversariantes">
            <Cake className="h-5 w-5 text-orange-600" />
            <AlertTitle className="text-orange-800 font-semibold">Aniversariantes da Semana!</AlertTitle>
            <AlertDescription className="text-orange-700">
              <div className="mt-2 space-y-2">
                {dashboard.aniversariantes.map((aluno) => (
                  <div key={aluno.id} className="flex items-center gap-2 bg-white p-2 rounded-lg" data-testid={`aniversariante-${aluno.id}`}>
                    <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-300 font-semibold">
                      {aluno.data_aniversario}
                    </Badge>
                    <span className="font-medium">{aluno.nome}</span>
                  </div>
                ))}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {dashboard?.alunos_faltosos && dashboard.alunos_faltosos.length > 0 && mostrarAlertaFaltas && (
          <Alert className="border-amber-200 bg-amber-50" data-testid="alert-faltosos">
            <div className="flex items-start justify-between w-full">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <AlertTitle className="text-amber-800 font-semibold">Atenção: Alunos com faltas</AlertTitle>
                  <AlertDescription className="text-amber-700">
                    <p className="text-sm mb-3">Os alunos abaixo possuem 3 ou mais faltas consecutivas.</p>
                    <div className="space-y-2">
                      {dashboard.alunos_faltosos
                        .filter(aluno => !alertasFaltasOcultos.includes(aluno.id))
                        .map((aluno) => (
                        <div key={aluno.id} className="flex flex-col sm:flex-row sm:items-center justify-between bg-white p-3 rounded-lg gap-2" data-testid={`faltoso-${aluno.id}`}>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{aluno.nome}</span>
                            <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                              {aluno.num_faltas} faltas
                            </Badge>
                          </div>
                          <div className="flex gap-2">
                            {aluno.responsavel_telefone && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600 border-green-300 hover:bg-green-50"
                                onClick={() => {
                                  const telefone = aluno.responsavel_telefone.replace(/\D/g, '');
                                  const mensagem = encodeURIComponent(
                                    `Olá! Gostaríamos de informar que ${aluno.nome} possui ${aluno.num_faltas} faltas consecutivas. Por favor, entre em contato conosco para regularizar a situação. Obrigado!`
                                  );
                                  window.open(`https://wa.me/55${telefone}?text=${mensagem}`, '_blank');
                                }}
                              >
                                <MessageCircle className="w-4 h-4 mr-1" />
                                <span className="hidden sm:inline">WhatsApp</span>
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-gray-500 hover:text-gray-700"
                              onClick={() => {
                                setAlertasFaltasOcultos(prev => [...prev, aluno.id]);
                                toast.success(`Alerta de ${aluno.nome} ocultado`);
                              }}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      {dashboard.alunos_faltosos.filter(a => !alertasFaltasOcultos.includes(a.id)).length === 0 && (
                        <p className="text-center text-amber-600 py-2">Todos os alertas foram ocultados</p>
                      )}
                    </div>
                  </AlertDescription>
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="text-amber-600 hover:text-amber-800 -mt-1"
                onClick={() => setMostrarAlertaFaltas(false)}
              >
                <EyeOff className="w-4 h-4" />
              </Button>
            </div>
          </Alert>
        )}
        
        {!mostrarAlertaFaltas && dashboard?.alunos_faltosos?.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="text-amber-600"
            onClick={() => {
              setMostrarAlertaFaltas(true);
              setAlertasFaltasOcultos([]);
            }}
          >
            <Eye className="w-4 h-4 mr-1" />
            Mostrar alertas de faltas ({dashboard.alunos_faltosos.length})
          </Button>
        )}

        {mensalidades?.vencidas && mensalidades.vencidas.length > 0 && (
          <Alert className="border-red-200 bg-red-50" data-testid="alert-mensalidades-vencidas">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <AlertTitle className="text-red-800 font-semibold">Mensalidades Vencidas!</AlertTitle>
            <AlertDescription className="text-red-700">
              <div className="mt-2 space-y-2">
                {mensalidades.vencidas.slice(0, 5).map((item) => (
                  <div key={item.aluno_id} className="flex items-center justify-between bg-white p-3 rounded-lg" data-testid={`mensalidade-vencida-${item.aluno_id}`}>
                    <div>
                      <div className="font-medium">{item.aluno_nome}</div>
                      <div className="text-sm text-gray-600">Responsável: {item.responsavel}</div>
                    </div>
                    <Badge variant="destructive">{item.dias_atraso} dias de atraso</Badge>
                  </div>
                ))}
                {mensalidades.vencidas.length > 5 && (
                  <div className="text-sm text-center text-gray-500 mt-2">
                    +{mensalidades.vencidas.length - 5} mensalidades vencidas
                  </div>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}
      </div>
    </Layout>
  );
}
